import { PRACTICE_ID } from "./practice";
import type { TourEvent } from "./tourEvents";

// The HMP help tours ("Hulp", the ? button). Each step points at an element
// marked data-tour="<target>" and either explains it (Volgende) or waits for
// the user to do something: click it, or until `until` holds. The engine
// lives in components/help/HelpProvider.tsx.

/** What a step's conditions can look at. */
export interface TourCtx {
  path: string;
  /** "Echt" was chosen at a practice/real choice (false while practising). */
  real: boolean;
  /** An element with this data-tour target is on screen. */
  has: (target: string) => boolean;
  /** The trimmed value of the first input inside the target. */
  value: (target: string) => string;
  /** The visible text of the target. */
  text: (target: string) => string;
  /** The data-tour-count attribute of the target. */
  count: (target: string) => number;
  /** The target contains a checked checkbox. */
  checked: (target: string) => boolean;
  /** The event happened since this step started. */
  since: (event: TourEvent) => boolean;
  /** The event happened since this tour started. */
  sinceStart: (event: TourEvent) => boolean;
  /** The HMP's own vacature list, /vacatures/<partner_id>. */
  vacaturesPath: string;
  /** Name(s) of the HMP's Waves contact, for "… krijgt een melding". */
  contact: string;
}

type Dyn<T> = T | ((c: TourCtx) => T);

export interface TourStep {
  /** data-tour target to spotlight; omitted for a centered card. */
  target?: string;
  title: Dyn<string>;
  text: Dyn<string>;
  /** The user continues by clicking the target (no Volgende button). */
  click?: boolean;
  /** The user continues by doing something (no Volgende button); pair with `until`. */
  act?: boolean;
  /** Advance as soon as this holds. */
  until?: (c: TourCtx) => boolean;
  /** Must hold before Volgende/a click continues; otherwise `err` shows. */
  ok?: (c: TourCtx) => boolean;
  err?: Dyn<string>;
  /** The step only makes sense while this holds; once it stops holding the tour stops (unless `ok`/`until` holds). */
  need?: (c: TourCtx) => boolean;
  /** Go back one step when this holds (e.g. a dialog was cancelled). */
  back?: (c: TourCtx) => boolean;
  skip?: (c: TourCtx) => boolean;
  /** Navigate here when the step starts (if not already there). */
  go?: string;
  /** The practice/real choice ("Oefenen of echt?"). */
  choice?: boolean;
  noPrev?: boolean;
  place?: "above";
}

export interface Tour {
  id: string;
  label: string;
  /** Starts with the practice/real choice; practising uses an Oefenvacature (lib/practice.ts). */
  practice?: "vacature" | "create";
  /** Label of the "real" button at the choice. */
  realLabel?: string;
  steps: TourStep[];
}

// ── Route helpers ─────────────────────────────────────────────

const onDashboard = (c: TourCtx) => c.path === "/";
const onVacatures = (c: TourCtx) => c.path === c.vacaturesPath;
const onDetail = (c: TourCtx) => c.path.startsWith("/vacature/");
const onPracticeDetail = (c: TourCtx) => c.path === `/vacature/${PRACTICE_ID}`;
/** On the vacature the tour works on: the practice one, or (real) any real one. */
const onChosenDetail = (c: TourCtx) => (c.real ? onDetail(c) && !onPracticeDetail(c) : onPracticeDetail(c));
const onMeldingen = (c: TourCtx) => c.path === "/meldingen";
const onReview = (c: TourCtx) => c.path === "/swipe-review";
const settingsOpen = (c: TourCtx) => c.has("settings");
const editOpen = (c: TourCtx) => c.has("edit-form");

const practiceDone = "Dit was een oefening, er is niets gewijzigd.";

// ── Shared steps ──────────────────────────────────────────────

const choiceStep = (text: string): TourStep => ({ choice: true, title: "Oefenen of echt?", text });

const openVacatures = (skip?: (c: TourCtx) => boolean): TourStep => ({
  target: "menu-vac",
  click: true,
  skip: c => onVacatures(c) || !!skip?.(c),
  until: onVacatures,
  title: "Open Vacatures",
  text: "Klik in het menu onder Vacatures op Alle vestigingen.",
});

/** Open Vacatures, then the vacature to work on: the user's pick, or the Oefenvacature. */
const pickVacature = (verb: string): TourStep[] => [
  openVacatures(onChosenDetail),
  {
    target: "vac-list",
    act: true,
    skip: c => !c.real || onChosenDetail(c),
    need: onVacatures,
    until: onChosenDetail,
    title: "Kies de vacature",
    text: `Klik op de vacature die je wilt ${verb}.`,
  },
  {
    target: "vac-practice",
    click: true,
    skip: c => c.real || onPracticeDetail(c),
    need: onVacatures,
    until: onPracticeDetail,
    title: "Open de oefenvacature",
    text: "Klik op de Oefenvacature bovenaan de lijst.",
  },
];

const openEdit: TourStep = {
  target: "btn-edit",
  click: true,
  need: onChosenDetail,
  until: editOpen,
  title: "Bewerken",
  text: "Klik op Bewerken.",
};

const saveEdit: TourStep = {
  target: "edit-save",
  click: true,
  need: editOpen,
  until: c => c.since("vacature-updated"),
  err: "Opslaan lukte niet. Bekijk de melding in het venster.",
  title: "Opslaan",
  text: "Klik op Wijzigingen opslaan.",
};

const undoToast = (text: string): TourStep => ({
  target: "toast",
  skip: c => !c.has("toast"),
  title: "Ongedaan maken",
  text,
});

const openSettings: TourStep = {
  target: "menu-inst",
  click: true,
  skip: settingsOpen,
  until: settingsOpen,
  title: "Open Instellingen",
  text: "Klik in het menu op Instellingen.",
};

const settingsTab = (tab: string, content: string, label: string): TourStep => ({
  target: `settings-tab-${tab}`,
  click: true,
  skip: c => c.has(content),
  need: settingsOpen,
  until: c => c.has(content),
  title: label,
  text: `Klik op ${label}.`,
});

// ── Tours ─────────────────────────────────────────────────────

export const TOURS: Tour[] = [
  {
    id: "intro",
    label: "Rondleiding door het portaal",
    steps: [
      { target: "dash-tiles", go: "/", need: onDashboard, title: "Overzicht", text: "Hier zie je hoeveel vacatures er openstaan, hoeveel urgentie hebben en hoeveel er al langer dan 30 dagen openstaan. Klik op een tegel om de tabel eronder te filteren." },
      { target: "dash-check", need: onDashboard, title: "Vacaturecheck", text: c => c.has("start-check") ? "Hier zie je hoeveel vacatures je moet controleren. Met Start de check loop je ze één voor één door." : "Hier zie je of er vacatures zijn die je moet controleren. Nu is alles actueel; zodra er iets te controleren is, krijg je een e-mail en staat hier een knop." },
      { target: "dash-table", need: onDashboard, title: "Jouw vacatures", text: "Hier staan de vacatures die jij hebt aangemaakt of gewijzigd. Klik op een vacature om hem te bekijken." },
      { target: "menu-vac", click: true, until: onVacatures, title: "Vacatures", text: "Klik in het menu onder Vacatures op Alle vestigingen." },
      { target: "vac-list", need: onVacatures, title: "Je vacatures", text: "Hier staan alle vacatures van je vestigingen. Zoek, filter op status of prioriteit, sorteer op een kolom en klik op een vacature om hem te bekijken of te bewerken." },
      { target: "vac-tabs", skip: c => !c.has("vac-tabs"), need: onVacatures, title: "Vestigingen", text: "Met deze tabs zie je alleen de vacatures van één vestiging. Mis je een vestiging? Vraag toegang aan via Instellingen." },
      { target: "menu-meldingen", click: true, until: onMeldingen, title: "Meldingen", text: "Klik in het menu op Meldingen." },
      { target: "notif-list", need: onMeldingen, title: "Meldingen", text: "Hier staan je meldingen, zoals wijzigingen aan vacatures." },
      { target: "bells", title: "Meldingen en taken", text: "Het belletje toont je laatste meldingen. Het icoon ernaast opent je taken: acties die op jou wachten." },
      { target: "help", title: "Hulp", text: "Via het vraagteken start je altijd een uitleg per onderwerp." },
      { target: "chatbar", skip: c => !c.has("chatbar"), title: "Chat met Waves", text: c => `Heb je een vraag over een vacature? Stel hem hier direct aan ${c.contact}.` },
      openSettings,
      { target: "settings-nav", need: settingsOpen, title: "Instellingen", text: "Hier beheer je je account, vraag je toegang tot vestigingen en kies je welke e-mails je krijgt." },
    ],
  },
  {
    id: "filter",
    label: "Zoeken en filteren",
    steps: [
      openVacatures(),
      { target: "vac-search", need: onVacatures, title: "Zoeken", text: 'Zoek op functietitel of locatie, bijvoorbeeld "werkvoorbereider". De lijst past zich direct aan.' },
      { target: "vac-tabs", skip: c => !c.has("vac-tabs"), need: onVacatures, title: "Vestiging", text: "Klik op een vestiging om alleen de vacatures daarvan te zien. Alle vestigingen toont alles." },
      { target: "filter-status", need: onVacatures, title: "Status", text: "Open het filter en kies Open, On hold of Gesloten." },
      { target: "filter-prio", need: onVacatures, title: "Prioriteit", text: "Open het filter en kies Hoog, Normaal of Laag." },
      { target: "vac-headers", need: onVacatures, title: "Sorteren", text: "Klik op een kolomkop, zoals Functie of Prioriteit. Klik nog een keer om de volgorde om te draaien." },
      { target: "vac-clear", skip: c => !c.has("vac-clear"), need: onVacatures, title: "Filters wissen", text: "Met Wis filters zet je zoeken en filters in één keer terug." },
    ],
  },
  {
    id: "new",
    label: "Vacature toevoegen",
    practice: "create",
    realLabel: "Echt aanmaken",
    steps: [
      choiceStep("Wil je oefenen, of echt een vacature aanmaken? Een oefening wordt aan het eind verwijderd."),
      openVacatures(),
      { target: "btn-new", click: true, need: onVacatures, until: editOpen, title: "Nieuwe vacature", text: "Klik op + Nieuwe vacature." },
      { target: "edit-title", need: editOpen, ok: c => !!c.value("edit-title"), err: "Vul eerst een functietitel in.", title: "Functietitel", text: "Vul de functietitel in. Zodra je typt, zie je suggesties die de andere velden alvast invullen." },
      { target: "edit-vest", need: editOpen, ok: c => !c.text("edit-vest").includes("Kies vestiging"), err: "Kies eerst een vestiging; dit veld is verplicht.", title: "Vestiging", text: "Kies de vestiging waar de vacature bij hoort. Dit veld is verplicht." },
      { target: "edit-disc", need: editOpen, title: "Discipline", text: "Kies een of meer disciplines die bij de functie passen." },
      { target: "edit-erv", need: editOpen, title: "Ervaring", text: "Kies het gewenste ervaringsniveau." },
      { target: "edit-afd", need: editOpen, title: "Afdeling", text: "Optioneel, bijvoorbeeld Infra. Soms staat er al een suggestie klaar." },
      { target: "edit-prio", need: editOpen, title: "Prioriteit", text: "Geef aan hoe dringend de vacature is: hoog, normaal of laag." },
      { target: "edit-spots", need: editOpen, title: "Aantal posities", text: "Geef aan hoeveel mensen je zoekt." },
      { target: "edit-sal", need: editOpen, place: "above", title: "Salarisindicatie", text: "Kies een bandbreedte, een vast bedrag of iets anders, en vul het bedrag in." },
      { target: "edit-note", need: editOpen, place: "above", title: "Omschrijving / opmerkingen", text: c => `Klap open om extra informatie voor ${c.contact} toe te voegen.` },
      { target: "edit-save", click: true, need: editOpen, until: c => c.since("vacature-created"), err: "Opslaan lukte niet. Bekijk de melding in het venster.", title: "Opslaan", text: "Klik op Publiceren om de vacature op te slaan." },
      { target: "vac-list", need: onVacatures, title: "Klaar", text: c => c.real ? `Je vacature is aangemaakt en staat in de lijst. ${c.contact} krijgt een melding.` : "Dit was een oefening, er is niets opgeslagen. De vacature verdwijnt zodra je op Klaar klikt." },
    ],
  },
  {
    id: "status",
    label: "Status of prioriteit wijzigen",
    practice: "vacature",
    steps: [
      choiceStep("Wil je oefenen met een oefenvacature, of een echte vacature aanpassen?"),
      ...pickVacature("aanpassen"),
      openEdit,
      { target: "edit-prio", need: editOpen, title: "Prioriteit", text: "Geef hier de prioriteit van de vacature aan: hoog, normaal of laag." },
      { target: "edit-status", need: editOpen, title: "Status", text: "Geef hier de status aan: open, on hold of gesloten. Alleen sluiten? Daar is ook een aparte knop voor, zie het hulponderwerp Vacature sluiten." },
      saveEdit,
      { target: "detail-badges", need: onDetail, title: "Klaar", text: c => c.real ? `De wijziging is opgeslagen. ${c.contact} krijgt een melding.` : practiceDone },
    ],
  },
  {
    id: "close",
    label: "Vacature sluiten",
    practice: "vacature",
    steps: [
      choiceStep("Wil je oefenen met een oefenvacature, of een echte vacature sluiten?"),
      ...pickVacature("sluiten"),
      { target: "btn-close", click: true, need: onChosenDetail, until: c => c.since("vacature-updated"), title: "Sluiten", text: "Klik op Vacature sluiten. Waves stopt dan met zoeken en de vacature komt niet meer in de vacaturecheck." },
      undoToast("Per ongeluk gesloten? Klik in deze melding op Ongedaan maken."),
      { target: "detail-badges", need: onDetail, title: "Klaar", text: c => c.real ? "De vacature is gesloten. Je vindt hem terug in de lijst via het filter Gesloten. Toch weer nodig? Klik op Vacature heropenen. Meerdere vacatures tegelijk sluiten? Gebruik het hulponderwerp Meerdere vacatures tegelijk bewerken." : practiceDone },
    ],
  },
  {
    id: "bem",
    label: "Kandidaten in bemiddeling bijwerken",
    practice: "vacature",
    steps: [
      choiceStep("Wil je oefenen met een oefenvacature, of een echte vacature bijwerken?"),
      ...pickVacature("bijwerken"),
      openEdit,
      { target: "edit-bem", need: editOpen, title: "Bemiddeling", text: "Kies In bemiddeling als Waves een kandidaat bij je heeft voorgesteld of als je met een kandidaat in gesprek bent. Staat het al goed? Klik dan op Volgende." },
      saveEdit,
      { target: "detail-badges", need: onDetail, title: "Klaar", text: c => c.real ? `De bemiddeling is bijgewerkt. ${c.contact} krijgt een melding.` : practiceDone },
    ],
  },
  {
    id: "confirm",
    label: "Vacature bevestigen (klopt nog)",
    practice: "vacature",
    steps: [
      choiceStep("Wil je oefenen met een oefenvacature, of een echte vacature bevestigen?"),
      ...pickVacature("bevestigen"),
      { target: "btn-confirm", click: true, need: onChosenDetail, until: c => c.since("vacature-updated"), title: "Bevestigen", text: "Klopt alles nog? Klik op Bevestig actueel. De teller 'dagen niet bevestigd' gaat dan op nul en de vacature komt niet in de vacaturecheck. Klopt er iets niet? Gebruik dan Bewerken." },
      { target: "detail-confirmed", need: onDetail, title: "Klaar", text: c => c.real ? "De vacature is bevestigd als actueel. Tip: in de vacaturecheck bevestig je door naar rechts te swipen." : practiceDone },
    ],
  },
  {
    id: "delete",
    label: "Vacature verwijderen",
    practice: "vacature",
    steps: [
      choiceStep("Wil je oefenen, of echt een vacature verwijderen?"),
      ...pickVacature("verwijderen"),
      { target: "btn-delete", click: true, need: onChosenDetail, until: c => c.has("delete-dialog"), title: "Verwijderen", text: c => "Klik op Verwijder vacature. Verwijderen haalt de vacature helemaal weg. Wil je hem bewaren? Sluit hem dan." + (c.real ? " Let op: dit is een echte vacature, en verwijderen kun je niet ongedaan maken." : "") },
      { target: "delete-dialog", act: true, until: c => c.since("vacature-deleted"), back: c => !c.has("delete-dialog"), title: "Bevestigen", text: "Klik op Verwijder om door te gaan, of op Annuleren als je twijfelt." },
      { title: "Klaar", noPrev: true, text: c => c.real ? "De vacature is verwijderd en staat niet meer in de lijst." : "Dit was een oefening, er is niets verwijderd." },
    ],
  },
  {
    id: "bulk",
    label: "Meerdere vacatures tegelijk bewerken",
    practice: "vacature",
    steps: [
      choiceStep("Wil je oefenen met een oefenvacature, of echte vacatures aanpassen?"),
      openVacatures(),
      { target: "bulk-start", click: true, skip: c => c.has("bulk-bar"), need: onVacatures, until: c => c.has("bulk-bar"), title: "Selecteren", text: "Klik op Meerdere aanpassen." },
      { target: "vac-list", skip: c => !c.real, need: c => c.has("bulk-bar"), ok: c => c.count("bulk-bar") > 0, err: "Vink minstens één vacature aan.", title: "Vink aan", text: "Vink de vacatures aan die je wilt aanpassen. Met Alles op deze pagina selecteren kies je in één keer alles wat je ziet. Klik daarna op Volgende." },
      { target: "vac-practice", skip: c => c.real, need: c => c.has("bulk-bar"), ok: c => c.count("bulk-bar") === 1 && c.checked("vac-practice"), err: "Vink alleen de oefenvacature aan.", title: "Vink aan", text: "Vink de oefenvacature aan. Normaal vink je hier alle vacatures aan die je wilt aanpassen." },
      { target: "bulk-edit", click: true, need: c => c.has("bulk-bar"), until: c => c.has("bulk-form"), err: "Vink eerst een vacature aan.", title: "Aanpassen", text: "Klik op Aanpassen." },
      { target: "bulk-form", need: c => c.has("bulk-form"), title: "Kies de wijziging", text: "Kies alleen wat je wilt veranderen. Wat op Niet wijzigen staat, blijft per vacature hetzelfde." },
      { target: "bulk-next", click: true, need: c => c.has("bulk-form"), until: c => c.has("bulk-apply"), title: "Controleren", text: "Kies een nieuwe status of prioriteit en klik op Controleren." },
      { target: "bulk-apply", click: true, need: c => c.has("bulk-apply"), until: c => c.since("vacature-updated"), title: "Bevestigen", text: "Hier zie je per vacature wat er verandert. Klopt het? Klik op Bevestig. Anders ga je met Terug terug." },
      undoToast("Vergist? Klik op Ongedaan maken."),
      { target: "vac-list", need: onVacatures, title: "Klaar", text: c => c.real ? `De wijzigingen zijn doorgevoerd. ${c.contact} krijgt een melding.` : practiceDone },
    ],
  },
  {
    id: "export",
    label: "Exporteren",
    steps: [
      openVacatures(),
      { target: "btn-export", click: true, need: onVacatures, until: c => c.since("export"), title: "Exporteren", text: "Klik op Export. Je downloadt een Excel-bestand met al je vacatures, ook als er filters aanstaan." },
    ],
  },
  {
    id: "review",
    label: "Vacaturecheck (swipen)",
    steps: [
      { target: "menu-dash", click: true, skip: c => onDashboard(c) || onReview(c), until: onDashboard, title: "Open Dashboard", text: "Klik in het menu op Dashboard." },
      { target: "dash-check", skip: onReview, need: onDashboard, title: "De vacaturecheck", text: c => c.has("start-check") ? "Hier zie je hoeveel vacatures je moet controleren." : "Er staat nu geen vacaturecheck voor je klaar. Zodra er vacatures te controleren zijn, krijg je een e-mail en staat hier een knop Start de check." },
      { target: "start-check", click: true, skip: c => onReview(c) || !c.has("start-check"), need: onDashboard, until: onReview, title: "Start de check", text: "Klik op Start de check." },
      { target: "review-progress", skip: c => !onReview(c), need: onReview, title: "Voortgang", text: "Hier zie je hoeveel vacatures je al hebt gedaan. Met Terug naar dashboard stop je; wat je al hebt beoordeeld, is opgeslagen." },
      { target: "review-card", act: true, skip: c => !c.has("review-card"), need: onReview, until: c => c.since("vacature-updated") || c.has("review-panel"), title: "Swipe de vacature", text: "Swipe naar rechts als de vacature nog klopt, naar links als er iets moet veranderen. Je kunt ook de knoppen of de pijltjestoetsen gebruiken." },
      { target: "review-panel", act: true, skip: c => !c.has("review-panel"), until: c => c.since("vacature-updated"), back: c => !c.has("review-panel"), title: "Pas de vacature aan", text: "Wijzig wat niet klopt: status, prioriteit, bemiddeling of een toelichting. Klik daarna op Opslaan en volgende." },
      undoToast("Per ongeluk verkeerd geswiped? Klik in deze melding op Ongedaan maken, dan komt de vacature terug."),
      { target: "review-card", skip: c => !c.has("review-card"), need: onReview, title: "Zo werkt het", text: "Je kunt nu verder met de rest, of stoppen en later verdergaan." },
    ],
  },
  {
    id: "chat",
    label: "Vraag stellen over een vacature",
    steps: [
      { target: "chatbar", click: true, skip: c => c.has("chat-window"), until: c => c.has("chat-window"), title: "Open de chat", text: "Klik op Berichten." },
      { target: "chat-vac-btn", click: true, skip: c => c.has("chat-picker"), need: c => c.has("chat-window"), until: c => c.has("chat-picker"), title: "Vraag over een vacature", text: "Klik op Stel een vraag over een vacature." },
      { target: "chat-picker", act: true, need: c => c.has("chat-window"), until: c => c.has("chat-input") && !c.has("chat-picker"), back: c => !c.has("chat-picker") && !c.has("chat-input"), title: "Kies de vacature", text: c => `Klik op de vacature waar je vraag over gaat. ${c.contact} ziet dan direct om welke vacature het gaat.` },
      { target: "chat-input", need: c => c.has("chat-window"), title: "Stel je vraag", text: "Typ hier je vraag en klik op Stuur. Je hoeft nu niets te versturen; klik op Volgende om verder te gaan." },
      { target: "chat-thread", need: c => c.has("chat-window"), title: "Antwoord", text: c => `${c.contact} antwoordt hier. Is de chat dicht, dan zie je een rood getal bij Berichten.` },
      { target: "chat-thread", need: c => c.has("chat-window"), title: "Reageren op een bericht", text: "Wil je op een specifiek bericht reageren? Ga met je muis over het bericht en klik op het pijltje ernaast; het citaat staat dan boven je invoerveld." },
    ],
  },
  {
    id: "access",
    label: "Toegang vragen tot een vestiging",
    steps: [
      openSettings,
      settingsTab("toegang", "settings-branches", "Vestigingen"),
      { target: "settings-branches", need: settingsOpen, title: "Vestigingen", text: "✓ Toegang: je ziet de vacatures van deze vestiging.\nToegang aangevraagd: Waves beoordeelt je verzoek.\nVraag toegang: je hebt nog geen toegang." },
      { target: "settings-branches", need: settingsOpen, until: c => c.since("branch-requested"), title: c => c.has("branch-request") ? "Toegang vragen" : "Niets aan te vragen", text: c => c.has("branch-request") ? "Klik op Vraag toegang bij de vestiging die je nodig hebt. Wil je alleen kijken? Klik dan op Volgende." : "Je hebt al toegang tot alle vestigingen, of er loopt al een aanvraag." },
      { target: "settings-branches", noPrev: true, skip: c => !c.sinceStart("branch-requested"), need: settingsOpen, title: "Wat nu?", text: "Je verzoek staat op Toegang aangevraagd. Waves beoordeelt het en je krijgt een melding zodra je toegang hebt." },
    ],
  },
  {
    id: "prefs",
    label: "Eigen meldingsinstellingen",
    steps: [
      openSettings,
      settingsTab("meldingen", "settings-alerts", "Meldingen"),
      { target: "settings-alerts", need: settingsOpen, title: "E-mailmeldingen", text: "Kies per onderwerp of je een melding krijgt, of je er ook een e-mail over wilt en hoe vaak. De meldingen bij het belletje blijven altijd zichtbaar." },
      { ...settingsTab("review", "settings-review", "Vacature-review"), skip: c => c.has("settings-review") || !c.has("settings-tab-review") },
      { target: "settings-review", skip: c => !c.has("settings-review"), need: settingsOpen, title: "Vacature-review", text: "Kies hier na hoeveel dagen een vacature in je vacaturecheck komt, en voor welke statussen en prioriteiten." },
      { target: "settings-footer", need: settingsOpen, until: c => c.since("settings-saved"), title: "Opslaan", text: "Iets gewijzigd? Klik op Opslaan om het te bewaren. Niets gewijzigd? Klik op Volgende." },
      { title: "Klaar", noPrev: true, text: "Je meldingen en e-mails komen voortaan zoals je hebt ingesteld." },
    ],
  },
  {
    id: "notif",
    label: "Meldingen en taken",
    steps: [
      { target: "menu-meldingen", click: true, skip: onMeldingen, until: onMeldingen, title: "Meldingen", text: "Klik in het menu op Meldingen." },
      { target: "notif-first", skip: c => !c.has("notif-first"), need: onMeldingen, title: "Een melding", text: "Een blauwe stip betekent ongelezen. Klik op een melding om de vacature te openen; met het kruisje markeer je hem als gelezen." },
      { target: "notif-allread", skip: c => !c.has("notif-allread"), need: onMeldingen, title: "Alles als gelezen markeren", text: "Alles bekeken? Met deze knop markeer je alles in één keer als gelezen." },
      { target: "tasks-bell", click: true, until: c => c.has("tasks-drop"), title: "Taken", text: "Klik op het taken-icoon." },
      { target: "tasks-drop", skip: c => !c.has("tasks-drop"), title: "Je taken", text: "Hier staan de acties die op jou wachten, zoals een vacaturecheck. Het getal bij het icoon laat zien hoeveel het er zijn." },
      { ...openSettings, title: "Minder e-mails?" },
      settingsTab("meldingen", "settings-alerts", "Meldingen"),
      { target: "settings-alerts", need: settingsOpen, title: "E-mailmeldingen", text: "Zet hier per onderwerp aan of uit of je er een melding of e-mail over krijgt." },
    ],
  },
];

/** The tours listed under "Op deze pagina" in the Hulp menu, by route. */
export function pageTours(path: string, vacaturesPath: string): string[] {
  if (path === "/") return ["intro", "review", "notif", "chat"];
  if (path === vacaturesPath) return ["filter", "new", "status", "close", "bulk", "export"];
  if (path.startsWith("/vacature/")) return ["status", "close", "bem", "confirm", "delete", "chat"];
  if (path === "/meldingen") return ["notif"];
  if (path === "/swipe-review") return ["review"];
  return [];
}

export function findTour(id: string): Tour | undefined {
  return TOURS.find(t => t.id === id);
}

export function resolveDyn<T>(v: Dyn<T>, c: TourCtx): T {
  return typeof v === "function" ? (v as (c: TourCtx) => T)(c) : v;
}
